import { Box, Container, Typography, Stack, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from '../components/SEOHead';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PublicIcon from '@mui/icons-material/Public';
import GroupWorkIcon from '@mui/icons-material/GroupWork';

const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(12, 0),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 0),
  },
}));

const GlassCard = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.05)'
    : 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  padding: theme.spacing(4),
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px rgba(0, 0, 0, 0.3)'
    : '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 12px 48px rgba(0, 0, 0, 0.4)'
      : '0 12px 48px rgba(0, 0, 0, 0.15)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 24px rgba(0, 0, 0, 0.3)'
    : '0 8px 24px rgba(0, 0, 0, 0.15)',
}));

export default function MissionVision() {
  const { t } = useLanguage();

  const goals = [
    {
      icon: <EmojiEventsIcon sx={{ fontSize: 40 }} />,
      title: t('mission.goals.1'),
      desc: t('mission.goals.1.desc'),
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      title: t('mission.goals.2'),
      desc: t('mission.goals.2.desc'),
    },
    {
      icon: <GroupWorkIcon sx={{ fontSize: 40 }} />,
      title: t('mission.goals.3'),
      desc: t('mission.goals.3.desc'),
    },
    {
      icon: <PublicIcon sx={{ fontSize: 40 }} />,
      title: t('mission.goals.4'),
      desc: t('mission.goals.4.desc'),
    },
  ];

  return (
    <>
      <SEOHead titleKey="mission.title" descriptionKey="mission.our.desc" />
      <Box component="main" role="main">
        {/* Hero Section */}
        <PageSection
          sx={{
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
                : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            pt: { xs: 16, md: 20 },
          }}
        >
          <Container maxWidth="lg">
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 700,
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('mission.title')}
              </Typography>
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ maxWidth: '800px', fontSize: '1.25rem' }}
              >
                {t('mission.subtitle')}
              </Typography>
            </Stack>
          </Container>
        </PageSection>

        {/* Mission Section */}
        <PageSection>
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Stack spacing={3}>
                  <IconWrapper>
                    <TrackChangesIcon sx={{ fontSize: 48, color: 'white' }} />
                  </IconWrapper>
                  <Typography variant="h2" fontWeight={700}>
                    {t('mission.our.title')}
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}
                  >
                    {t('mission.our.desc')}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <GlassCard
                  sx={{
                    height: '400px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: (theme) =>
                      `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
                  }}
                >
                  <TrackChangesIcon
                    sx={{
                      fontSize: 200,
                      color: 'primary.main',
                      opacity: 0.3,
                    }}
                  />
                </GlassCard>
              </Grid>
            </Grid>
          </Container>
        </PageSection>

        {/* Vision Section */}
        <PageSection
          sx={{
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.02)'
                : 'rgba(0, 0, 0, 0.02)',
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                <GlassCard
                  sx={{
                    height: '400px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: (theme) =>
                      `linear-gradient(135deg, ${theme.palette.secondary.main}15 0%, ${theme.palette.primary.main}15 100%)`,
                  }}
                >
                  <VisibilityIcon
                    sx={{
                      fontSize: 200,
                      color: 'secondary.main',
                      opacity: 0.3,
                    }}
                  />
                </GlassCard>
              </Grid>
              <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                <Stack spacing={3}>
                  <IconWrapper>
                    <VisibilityIcon sx={{ fontSize: 48, color: 'white' }} />
                  </IconWrapper>
                  <Typography variant="h2" fontWeight={700}>
                    {t('vision.our.title')}
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}
                  >
                    {t('vision.our.desc')}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </PageSection>

        {/* Strategic Goals */}
        <PageSection>
          <Container maxWidth="lg">
            <Stack spacing={6}>
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Typography variant="h2" fontWeight={700}>
                  {t('mission.goals.title')}
                </Typography>
              </Stack>
              <Grid container spacing={4}>
                {goals.map((goal, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <GlassCard sx={{ height: '100%' }}>
                      <Stack spacing={2} alignItems="center" textAlign="center">
                        <Box
                          sx={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: (theme) =>
                              `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.secondary.main}20 100%)`,
                            color: 'primary.main',
                          }}
                        >
                          {goal.icon}
                        </Box>
                        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
                          {goal.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                          {goal.desc}
                        </Typography>
                      </Stack>
                    </GlassCard>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Container>
        </PageSection>

        {/* Approach Section */}
        <PageSection
          sx={{
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.02)'
                : 'rgba(0, 0, 0, 0.02)',
          }}
        >
          <Container maxWidth="lg">
            <GlassCard sx={{ textAlign: 'center', py: 6 }}>
              <Stack spacing={3} alignItems="center">
                <Typography variant="h3" fontWeight={700}>
                  {t('mission.approach.title')}
                </Typography>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ maxWidth: '800px', lineHeight: 1.8, fontSize: '1.1rem' }}
                >
                  {t('mission.approach.desc')}
                </Typography>
              </Stack>
            </GlassCard>
          </Container>
        </PageSection>
      </Box>
    </>
  );
}